import {
  WeeklyDateNavigationFirstDayOfWeek,
  WeeklyDateNavigationLanguageType,
} from '../types';
import { endOfWeek, startOfWeek, isSameWeek, addWeeks } from 'date-fns';
import { WeekDatesRange } from '../../WeeklyDatePicker';

export const languages = {
  ar: 'ar',
  bg: 'bg',
  cs: 'cs',
  da: 'da',
  de: 'de',
  el: 'el',
  en: 'en',
  es: 'es',
  fi: 'fi',
  fr: 'fr',
  he: 'he',
  hi: 'hi',
  hu: 'hu',
  id: 'id',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  lt: 'lt',
  ms: 'ms',
  nl: 'nl',
  no: 'no',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  ru: 'ru',
  sv: 'sv',
  th: 'th',
  tl: 'tl',
  tr: 'tr',
  uk: 'uk',
  vi: 'vi',
  zh: 'zh',
};

export const extractLanguageCode = (locale: string) => {
  return locale.split('-')[0] as keyof typeof languages;
};

const FIRST_WEEKDAY = {
  vi: 0,
};

export const getFirstDayOfWeek = (
  locale,
  firstDayOfWeek
): 1 | 2 | 3 | 4 | 5 | 6 | 0 =>
  firstDayOfWeek ?? FIRST_WEEKDAY[extractLanguageCode(locale)] ?? 1;

export const CAPITALIZED_MONTH_LANGUAGES = [
  'es',
  'de',
  'fr',
  'ru',
  'it',
  'uk',
  'nl',
  'tr',
  'pl',
  'cs',
  'no',
  'pt',
];

export function capitalizeFirstLetter(str: string) {
  if (!str) {
    return undefined;
  }
  return str[0].toLocaleUpperCase() + str.substring(1);
}

export const capitalizeMonth = (month, language) => {
  const shouldCapitalizeMonth = CAPITALIZED_MONTH_LANGUAGES.includes(language);

  if (shouldCapitalizeMonth) {
    return capitalizeFirstLetter(month);
  }
  return month;
};

export const formatMonthAndYear = (locale, date) => {
  return capitalizeMonth(
    new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
    }).format(date),
    extractLanguageCode(locale)
  );
};

const formatShortMonth = (locale, date) => {
  return capitalizeMonth(
    new Intl.DateTimeFormat(locale, {
      month: 'short',
    }).format(date),
    extractLanguageCode(locale)
  );
};

const formatShortMonthAndYear = (locale, date) => {
  return capitalizeMonth(
    new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
    }).format(date),
    extractLanguageCode(locale)
  );
};

export const formatMonthsRange = (locale, firstDate, secondDate) => {
  const firstMonth = formatShortMonth(locale, firstDate);
  const secondMonthAndYear = formatShortMonthAndYear(locale, secondDate);
  return `${firstMonth} - ${secondMonthAndYear}`;
};

export const getWeekDatesRange = (
  date: Date,
  firstDayOfWeek: WeeklyDateNavigationFirstDayOfWeek = 1
) => {
  const startOfTheWeek = startOfWeek(date, { weekStartsOn: firstDayOfWeek });
  const endOfTheWeek = endOfWeek(date, { weekStartsOn: firstDayOfWeek });
  return { startOfWeek: startOfTheWeek, endOfWeek: endOfTheWeek };
};

export const areDatesInTheSameWeek = (
  firstDate: Date,
  secondDate: Date,
  firstDayOfWeek: WeeklyDateNavigationFirstDayOfWeek = 1
) => {
  return isSameWeek(firstDate, secondDate, { weekStartsOn: firstDayOfWeek });
};

export const getOffsetWeekDatesRange = (
  currentWeek: Date,
  offset: number,
  {
    locale,
    firstDayOfWeek,
  }: {
    locale: WeeklyDateNavigationLanguageType;
    firstDayOfWeek: WeeklyDateNavigationFirstDayOfWeek;
  }
) => {
  const firstDayOfTheWeek = getFirstDayOfWeek(locale, firstDayOfWeek);
  const newStartOfWeek = addWeeks(currentWeek, offset);
  return getWeekDatesRange(
    newStartOfWeek,
    firstDayOfTheWeek as WeeklyDateNavigationFirstDayOfWeek
  );
};

export const getCaptionText = (
  weekRange: WeekDatesRange,
  locale: WeeklyDateNavigationLanguageType
) => {
  if (weekRange.startOfWeek.getMonth() === weekRange.endOfWeek.getMonth()) {
    return formatMonthAndYear(locale, weekRange.endOfWeek);
  }
  return formatMonthsRange(locale, weekRange.startOfWeek, weekRange.endOfWeek);
};
